import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { UserPlus } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";

export const Route = createFileRoute("/_site/account/register")({
  head: () => ({
    meta: [
      { title: "Create your free account — NexKey.gg" },
      { name: "description", content: "Create a free NexKey account to save games and sync your wishlist." },
      { property: "og:title", content: "Create your free account — NexKey.gg" },
      { property: "og:description", content: "Create a free NexKey account to save games and sync your wishlist." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AccountRegister,
});

function AccountRegister() {
  const { t } = useSettings();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t("account.error.short"));
      return;
    }
    if (password !== confirm) {
      setError(t("account.error.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await register(email.trim(), password);
      navigate({ to: "/account", replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("account.error.register"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto grid place-items-center px-4 py-16">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-glow">
        <h1 className="flex items-center gap-2 font-display text-xl font-bold">
          <UserPlus className="h-5 w-5 text-primary" /> {t("account.register.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("account.register.subtitle")}</p>

        <label className="mt-6 block">
          <span className="text-xs font-medium text-muted-foreground">{t("account.email")}</span>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </label>
        <label className="mt-4 block">
          <span className="text-xs font-medium text-muted-foreground">{t("account.password")}</span>
          <input
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
          />
          <span className="mt-1 block text-[11px] text-muted-foreground">{t("account.password.hint")}</span>
        </label>
        <label className="mt-4 block">
          <span className="text-xs font-medium text-muted-foreground">{t("account.confirm")}</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
          />
        </label>

        {error && <p className="mt-4 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="mt-6 w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:brightness-110 disabled:opacity-60"
        >
          {loading ? t("account.loading") : t("account.register.cta")}
        </button>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          {t("account.register.haveAccount")}{" "}
          <Link to="/account/login" className="font-semibold text-primary hover:underline">
            {t("account.login.cta")}
          </Link>
        </p>
      </form>
    </div>
  );
}
